import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, Smartphone, RefreshCw, Mail, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { signInWithCustomToken } from "firebase/auth";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { auth } from "../config/firebase";
import { masjidApi } from "../services/api";
import { getSafeRedirect, buildRedirectQuery } from "../utils/safeRedirect";
import { oauthLabel, describeOAuthError, splitDisplayName } from "../utils/oauth";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

const inputClass =
  "h-12 rounded-xl border text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]";

export default function Login() {
  const palette = useEditorialPalette();
  const inputStyle = {
    background: palette.isDark ? palette.paper : "#ffffff",
    borderColor: palette.hairline,
    color: palette.ink,
    fontFamily: EDITORIAL_FONT.sans,
  };
  const location = useLocation();
  // Optional post-login redirect (e.g. /login?redirect=/solutions).
  // Only honored for internal paths to guard against open redirects.
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const safeRedirect = getSafeRedirect(redirectParam);
  const redirectQuery = buildRedirectQuery(safeRedirect);
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(location.state?.resetMode || false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [skipAutoRedirect, setSkipAutoRedirect] = useState(false);
  const [activeTab, setActiveTab] = useState("credentials");

  const [qrSession, setQrSession] = useState(null);
  const [qrStatus, setQrStatus] = useState("loading");
  const [qrTimeLeft, setQrTimeLeft] = useState(0);

  const {
    login,
    loginWithGoogle,
    loginWithApple,
    resetPassword,
    user,
    userProfile,
    loading: authLoading,
  } = useAuth();
  const navigate = useNavigate();

  /* ─── Redirect logic (unchanged) ─── */
  useEffect(() => {
    if (authLoading) return;
    if (skipAutoRedirect) return;

    if (user) {
      if (!user.emailVerified) {
        setRedirecting(true);
        navigate(`/signup${redirectQuery}`, {
          state: { startAtStep: 1, email: user.email },
        });
        return;
      }

      // Muslim+ Solutions customers are not part of the app role system, so
      // choosing community/masjid/halal is irrelevant to them. Someone heading
      // for the Solutions portal is let straight through rather than being
      // trapped in a role picker that does not apply.
      const headingToSolutions = safeRedirect?.startsWith("/solutions");

      if (userProfile && userProfile.signupComplete === false && !headingToSolutions) {
        setRedirecting(true);
        navigate(`/signup${redirectQuery}`, {
          state: { startAtStep: 2, email: user.email },
        });
        return;
      }

      if (userProfile) {
        if (safeRedirect) {
          setRedirecting(true);
          navigate(safeRedirect);
          return;
        }
        if (userProfile.role === "super") {
          setRedirecting(true);
          navigate("/dashboard");
          return;
        }
        if (userProfile.role === "halal") {
          setRedirecting(true);
          navigate("/halal");
          return;
        }
        if (userProfile.isAdmin || userProfile.role === "masjid") {
          setRedirecting(true);
          const isComplete = userProfile.signupComplete === true;
          navigate(isComplete ? "/masjid-dashboard" : "/masjid-signup");
          return;
        }
      }

      setRedirecting(true);
      navigate(safeRedirect || "/");
    }
  }, [user, userProfile, authLoading, navigate, skipAutoRedirect, safeRedirect, redirectQuery]);

  /* ─── QR session ─── */
  const createQrSession = useCallback(async () => {
    try {
      setQrStatus("loading");
      const response = await masjidApi.post("/v1/qr-session/create");
      setQrSession(response.data);
      setQrStatus("pending");
    } catch (err) {
      console.error("Failed to create QR session:", err);
      setQrStatus("error");
    }
  }, []);

  useEffect(() => {
    if (!resetMode && !user && !authLoading) {
      createQrSession();
    }
  }, [resetMode, user, authLoading, createQrSession]);

  /* ─── QR polling ─── */
  useEffect(() => {
    if (!qrSession || (qrStatus !== "pending" && qrStatus !== "scanned")) return;

    const poll = async () => {
      try {
        const response = await masjidApi.get(`/v1/qr-session/${qrSession.sessionId}/status`);

        if (response.data.status === "scanned" && qrStatus === "pending") {
          setQrStatus("scanned");
        } else if (response.data.status === "authenticated") {
          setQrStatus("signing-in");
          try {
            await signInWithCustomToken(auth, response.data.customToken);
            setQrStatus("authenticated");
          } catch (signInError) {
            console.error("Sign in with custom token failed:", signInError);
            setQrStatus("error");
          }
        } else if (response.data.status === "expired") {
          setQrStatus("expired");
        }
      } catch (err) {
        console.error("Poll error:", err);
      }
    };

    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [qrSession, qrStatus]);

  /* ─── QR countdown ─── */
  useEffect(() => {
    if (!qrSession) return;

    const updateTimer = () => {
      const remaining = Math.max(0, qrSession.expiresAt - Date.now());
      setQrTimeLeft(Math.ceil(remaining / 1000));
      if (remaining <= 0 && qrStatus === "pending") {
        setQrStatus("expired");
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [qrSession, qrStatus]);

  /* ─── Form submit ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (resetMode) {
        await resetPassword(email);
        setResetSuccess(true);
        setResetMode(false);
      } else {
        const userCredential = await login(email, password);
        if (!userCredential.emailVerified) {
          navigate(`/signup${redirectQuery}`, { state: { startAtStep: 1, email } });
          return;
        }
      }
    } catch (err) {
      let errorMessage = "Failed to sign in";
      if (err.code) {
        switch (err.code) {
          case "auth/invalid-credential":
          case "auth/wrong-password":
          case "auth/user-not-found":
            errorMessage = "Invalid email or password";
            break;
          case "auth/invalid-email":
            errorMessage = "Invalid email address";
            break;
          case "auth/user-disabled":
            errorMessage = "This account has been disabled";
            break;
          case "auth/too-many-requests":
            errorMessage = "Too many failed attempts. Please try again later";
            break;
          case "auth/network-request-failed":
            errorMessage = "Network error. Please check your connection";
            break;
          case "auth/unauthorized-domain":
          case "auth/operation-not-allowed":
            errorMessage = "Unable to sign in at this time. Please try again later";
            break;
          default:
            errorMessage = "Unable to sign in. Please try again";
            console.error("Login error:", err);
        }
      } else {
        errorMessage = "Unable to sign in. Please try again";
        console.error("Login error:", err);
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /* ─── OAuth sign-in (Google / Apple) ─── */
  const handleOAuthSignIn = async (providerId) => {
    const isApple = providerId === "apple";
    const label = oauthLabel(providerId);
    setError("");
    setLoading(true);
    setSkipAutoRedirect(true);

    try {
      const result = isApple ? await loginWithApple() : await loginWithGoogle();
      const { firstName, lastName } = splitDisplayName(result.user?.displayName);

      if (result.needsSignup === false && result.signupComplete === true) {
        setSkipAutoRedirect(false);
        navigate(safeRedirect || "/");
        return;
      }
      if (result.needsSignup === false && result.signupComplete === false) {
        setSkipAutoRedirect(false);
        return;
      }
      if (
        result.needsSignup === undefined &&
        !result.needsPhone &&
        !result.existingUser
      ) {
        setSkipAutoRedirect(false);
        navigate(safeRedirect || "/");
        return;
      }

      navigate(`/signup${redirectQuery}`, {
        state: {
          googleSignup: true,
          oauthProvider: providerId,
          firstName,
          lastName,
          email: result.user?.email || "",
          existingUserUid: result.existingUser?.uid || null,
          privateRelay: !!result.privateRelay,
        },
      });
    } catch (err) {
      setSkipAutoRedirect(false);
      if (!err?.code) console.error(`${label} sign-in error:`, err);
      setError(describeOAuthError(err, label));
      setLoading(false);
    }
  };

  /* ─── Redirecting state ─── */
  if (redirecting) {
    return (
      <EditorialShell className="flex items-center justify-center">
        <div className="text-center">
          <SerifHeading level={2} style={{ fontSize: 32, lineHeight: 1 }}>
            Signing you in…
          </SerifHeading>
          <p className="mt-3 text-sm" style={{ color: palette.muted }}>
            One moment while we check your account.
          </p>
        </div>
      </EditorialShell>
    );
  }

  /* ─── QR Code renderer ─── */
  const renderQrContent = () => {
    if (qrStatus === "loading") {
      return (
        <div className="flex h-52 w-52 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: BRAND.gold }} />
        </div>
      );
    }
    if (qrStatus === "pending" && qrSession) {
      return <QRCodeSVG value={qrSession.sessionId} size={208} level="M" includeMargin={false} />;
    }
    if (qrStatus === "scanned") {
      return (
        <div className="flex h-52 w-52 flex-col items-center justify-center">
          <Loader2 className="mb-3 h-10 w-10 animate-spin" style={{ color: BRAND.gold }} />
          <span className="text-sm font-medium" style={{ color: palette.muted }}>
            Device connected…
          </span>
        </div>
      );
    }
    if (qrStatus === "signing-in") {
      return (
        <div className="flex h-52 w-52 flex-col items-center justify-center">
          <Loader2 className="mb-3 h-10 w-10 animate-spin" style={{ color: BRAND.gold }} />
          <span className="text-sm font-medium" style={{ color: palette.muted }}>
            Signing you in…
          </span>
        </div>
      );
    }
    if (qrStatus === "authenticated") {
      return (
        <div className="flex h-52 w-52 flex-col items-center justify-center" style={{ color: BRAND.sage }}>
          <span className="mt-3 text-sm font-medium">Login successful</span>
        </div>
      );
    }
    return (
      <div className="flex h-52 w-52 flex-col items-center justify-center">
        <p className="mb-3 text-sm" style={{ color: palette.muted }}>
          {qrStatus === "expired" ? "QR code expired" : "Failed to load"}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={createQrSession}
          className="gap-2 rounded-full"
          style={{
            borderColor: palette.ink,
            color: palette.ink,
            background: "transparent",
            fontFamily: EDITORIAL_FONT.sans,
          }}
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>
    );
  };

  return (
    <EditorialShell>
      <div className="flex min-h-screen flex-col lg:flex-row">
        {/* ════════════ LEFT PANEL ════════════ */}
        <div
          className="relative hidden overflow-hidden lg:flex lg:w-[45%] xl:w-[42%] flex-col p-10 xl:p-14"
          style={{ background: palette.paper }}
        >
          <PatternTile opacity={0.06} />

          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Muslim+ home"
            className="relative z-10 flex items-center gap-2.5 self-start transition-opacity hover:opacity-80"
          >
            <span
              style={{
                fontFamily: EDITORIAL_FONT.sans,
                fontSize: 24,
                fontWeight: 500,
                lineHeight: 1,
                letterSpacing: "-0.015em",
                color: palette.ink,
              }}
            >
              Muslim+
            </span>
          </button>

          <div className="relative z-10 flex flex-1 items-center">
            <div>
              <Eyebrow>Welcome back</Eyebrow>
              <SerifHeading
                level={1}
                className="mt-6"
                style={{
                  fontSize: "clamp(2.75rem, 4.5vw, 4.25rem)",
                  lineHeight: 1,
                  letterSpacing: "-0.035em",
                }}
              >
                Your community,
                <br />
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>connected.</em>
              </SerifHeading>
              <p
                className="mt-6 max-w-md text-[15px]"
                style={{ color: palette.ink2, lineHeight: 1.55 }}
              >
                Prayer times, masajid, events, and halal. Signed in once, synced everywhere.
              </p>
            </div>
          </div>

        </div>

        {/* ════════════ RIGHT PANEL — form ════════════ */}
        <div className="relative flex min-h-screen flex-1 flex-col" style={{ background: palette.ivory }}>
          {/* Mobile top bar */}
          <div className="flex items-center justify-between px-5 pt-6 lg:hidden">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2.5"
              aria-label="Muslim+ home"
            >
              <span
                style={{
                  fontFamily: EDITORIAL_FONT.sans,
                  fontSize: 22,
                  fontWeight: 500,
                  lineHeight: 1,
                  letterSpacing: "-0.015em",
                  color: palette.ink,
                }}
              >
                Muslim+
              </span>
            </button>
          </div>

          <div className="hidden justify-end px-10 pt-8 lg:flex">
            <button
              onClick={() => navigate("/")}
              className="text-sm transition-colors hover:opacity-70"
              style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
            >
              ← Back to home
            </button>
          </div>

          <div className="flex flex-1 items-center justify-center px-5 py-10 sm:px-10 lg:px-16">
            <div className="w-full max-w-[520px]" style={{ animation: "editorialFadeUp 0.5s ease-out both" }}>
              <div className="mb-8">
                <Eyebrow>{resetMode ? "Reset access" : "Sign in"}</Eyebrow>
                <SerifHeading
                  level={2}
                  className="mt-4"
                  style={{
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    lineHeight: 1,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {resetMode ? (
                    <>
                      Reset your <em style={{ color: BRAND.gold, fontWeight: 400 }}>password.</em>
                    </>
                  ) : (
                    <>
                      Welcome <em style={{ color: BRAND.gold, fontWeight: 400 }}>back.</em>
                    </>
                  )}
                </SerifHeading>
                <p className="mt-4 text-[15px]" style={{ color: palette.muted }}>
                  {resetMode
                    ? "Enter your email and we'll send a reset link."
                    : "Sign in to your Muslim+ account."}
                </p>
              </div>

              {error && (
                <Alert
                  className="mb-5 rounded-xl"
                  style={{
                    background: BRAND.terracotta + "15",
                    borderColor: BRAND.terracotta + "40",
                    color: BRAND.terracotta,
                  }}
                >
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}
              {resetSuccess && (
                <Alert
                  className="mb-5 rounded-xl"
                  style={{
                    background: BRAND.sage + "15",
                    borderColor: BRAND.sage + "40",
                    color: BRAND.sage,
                  }}
                >
                  <AlertDescription className="text-sm">
                    Password reset email sent. Check your inbox.
                  </AlertDescription>
                </Alert>
              )}

              {resetMode ? (
                <div>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium" style={{ color: palette.ink }}>
                        Email address
                      </Label>
                      <div className="relative">
                        <Mail
                          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2"
                          style={{ color: palette.muted }}
                        />
                        <Input
                          id="email"
                          type="email"
                          placeholder="name@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          autoComplete="email"
                          autoFocus
                          className={`${inputClass} pl-10`}
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <PillButton
                      type="submit"
                      variant="primary"
                      disabled={loading}
                      className="w-full justify-center"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          Send reset link
                          <span aria-hidden>→</span>
                        </>
                      )}
                    </PillButton>
                  </form>

                  <button
                    type="button"
                    className="mt-4 w-full text-center text-sm transition-colors hover:opacity-70"
                    style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
                    onClick={() => {
                      setResetMode(false);
                      setError("");
                      setResetSuccess(false);
                    }}
                  >
                    ← Back to sign in
                  </button>
                </div>
              ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList
                    className="mb-8 grid h-12 w-full grid-cols-2 rounded-full p-1"
                    style={{ background: palette.paper }}
                  >
                    <TabsTrigger
                      value="credentials"
                      className="rounded-full text-sm font-medium transition-all data-[state=active]:shadow-sm"
                      style={{
                        fontFamily: EDITORIAL_FONT.sans,
                      }}
                    >
                      <Mail className="mr-1.5 h-3.5 w-3.5" />
                      Email
                    </TabsTrigger>
                    <TabsTrigger
                      value="qr"
                      className="rounded-full text-sm font-medium transition-all data-[state=active]:shadow-sm"
                      style={{ fontFamily: EDITORIAL_FONT.sans }}
                    >
                      <Smartphone className="mr-1.5 h-3.5 w-3.5" />
                      QR Code
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="credentials" className="mt-0 space-y-6">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium" style={{ color: palette.ink }}>
                          Email address
                        </Label>
                        <div className="relative">
                          <Mail
                            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2"
                            style={{ color: palette.muted }}
                          />
                          <Input
                            id="email"
                            type="email"
                            placeholder="name@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            autoFocus
                            className={`${inputClass} pl-10`}
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password" className="text-sm font-medium" style={{ color: palette.ink }}>
                            Password
                          </Label>
                          <button
                            type="button"
                            onClick={() => {
                              setResetMode(true);
                              setError("");
                              setResetSuccess(false);
                            }}
                            className="text-xs font-medium transition-colors hover:opacity-80"
                            style={{ color: BRAND.gold, fontFamily: EDITORIAL_FONT.sans }}
                          >
                            Forgot password?
                          </button>
                        </div>
                        <div className="relative">
                          <Lock
                            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2"
                            style={{ color: palette.muted }}
                          />
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSubmit(e);
                              }
                            }}
                            required
                            autoComplete="current-password"
                            className={`${inputClass} pl-10 pr-11`}
                            style={inputStyle}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors hover:opacity-80"
                            style={{ color: palette.muted }}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      <PillButton
                        type="submit"
                        variant="primary"
                        disabled={loading}
                        className="w-full justify-center"
                        size="lg"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in…
                          </>
                        ) : (
                          <>
                            Sign in
                            <span aria-hidden>→</span>
                          </>
                        )}
                      </PillButton>
                    </form>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t" style={{ borderColor: palette.hairline }} />
                      </div>
                      <div className="relative flex justify-center">
                        <span
                          className="px-3 text-xs font-medium uppercase"
                          style={{
                            background: palette.ivory,
                            color: palette.muted,
                            letterSpacing: "0.14em",
                          }}
                        >
                          or
                        </span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      className="h-12 w-full rounded-full text-[15px] font-semibold transition-all hover:opacity-90"
                      disabled={loading}
                      onClick={() => handleOAuthSignIn("google")}
                      style={{
                        background: "#ffffff",
                        borderColor: "#dadce0",
                        color: "#1a1713",
                        fontFamily: EDITORIAL_FONT.sans,
                      }}
                    >
                      <svg
                        className="mr-2.5 h-[18px] w-[18px]"
                        viewBox="0 0 18 18"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                          fill="#4285F4"
                        />
                        <path
                          d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z"
                          fill="#34A853"
                        />
                        <path
                          d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
                          fill="#EA4335"
                        />
                      </svg>
                      Continue with Google
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      className="mt-3 h-12 w-full rounded-full text-[15px] font-semibold transition-all hover:opacity-90"
                      disabled={loading}
                      onClick={() => handleOAuthSignIn("apple")}
                      style={{
                        background: "#000000",
                        borderColor: "#000000",
                        color: "#ffffff",
                        fontFamily: EDITORIAL_FONT.sans,
                      }}
                    >
                      <svg
                        className="mr-2.5 h-[18px] w-[18px]"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden
                      >
                        <path d="M16.365 1.43c0 1.14-.42 2.2-1.12 3.02-.85.99-2.24 1.76-3.4 1.67a3.6 3.6 0 0 1-.03-.42c0-1.1.5-2.24 1.2-3.02.79-.9 2.14-1.58 3.3-1.63.03.13.05.26.05.38zM20.9 17.1c-.57 1.32-.85 1.91-1.58 3.08-1.02 1.63-2.47 3.67-4.26 3.68-1.59.02-2-1.04-4.16-1.02-2.16.01-2.6 1.04-4.2 1.02-1.79-.01-3.16-1.85-4.19-3.48C-.4 16.83-.7 11.5 1.05 8.67c1.24-2.01 3.2-3.19 5.04-3.19 1.87 0 3.05 1.03 4.6 1.03 1.5 0 2.42-1.03 4.58-1.03 1.64 0 3.37.89 4.61 2.43-4.05 2.22-3.4 8-.98 9.19z" />
                      </svg>
                      Continue with Apple
                    </Button>

                    <p className="pt-1 text-center text-sm" style={{ color: palette.muted }}>
                      Don&apos;t have an account?{" "}
                      <button
                        type="button"
                        onClick={() => navigate(`/signup${redirectQuery}`)}
                        className="font-semibold transition-colors hover:underline"
                        style={{ color: BRAND.gold, fontFamily: EDITORIAL_FONT.sans }}
                      >
                        Sign up
                      </button>
                    </p>
                  </TabsContent>

                  <TabsContent value="qr" className="mt-0">
                    <div className="flex flex-col items-center py-2">
                      <div
                        className="mb-4 rounded-2xl border p-5 shadow-sm"
                        style={{ background: "#ffffff", borderColor: palette.hairline }}
                      >
                        {renderQrContent()}
                      </div>

                      {qrStatus === "pending" && (
                        <p className="mb-3 text-xs tabular-nums" style={{ color: palette.muted }}>
                          Expires in{" "}
                          <span className="font-medium">
                            {Math.floor(qrTimeLeft / 60)}:{String(qrTimeLeft % 60).padStart(2, "0")}
                          </span>
                        </p>
                      )}

                      <div className="mt-1 max-w-[280px] space-y-3 text-center">
                        <p className="text-sm leading-relaxed" style={{ color: palette.muted }}>
                          Open <span className="font-semibold" style={{ color: palette.ink }}>Muslim+</span>{" "}
                          app and tap{" "}
                          <span className="font-medium" style={{ color: palette.ink }}>
                            &quot;Web Login&quot;
                          </span>{" "}
                          in Settings to scan.
                        </p>

                        {[
                          "Open app → Settings",
                          'Tap "Web Login" and scan code',
                          "Confirm on your phone to sign in",
                        ].map((text, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-xs" style={{ color: palette.muted }}>
                            <div
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                              style={{ background: BRAND.gold + "20", color: BRAND.goldDeep }}
                            >
                              {idx + 1}
                            </div>
                            <span>{text}</span>
                          </div>
                        ))}
                      </div>

                      <p className="mt-6 text-center text-sm" style={{ color: palette.muted }}>
                        Don&apos;t have an account?{" "}
                        <button
                          type="button"
                          onClick={() => navigate(`/signup${redirectQuery}`)}
                          className="font-semibold transition-colors hover:underline"
                          style={{ color: BRAND.gold, fontFamily: EDITORIAL_FONT.sans }}
                        >
                          Sign up
                        </button>
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </div>

          <div className="px-5 pb-6 text-center lg:px-10 lg:pb-8 lg:text-right">
            <p className="text-xs" style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}>
              © {new Date().getFullYear()} Muslim+. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes editorialFadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </EditorialShell>
  );
}
